/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from "react";
import { Badge } from "reactstrap";

import { DEFAULT_FILTER_STATE, filterAndSortSignals } from "../signalFiltering";
import { useSignalSelection } from "../useSignalSelection";
import { CATEGORY_COLOR, CATEGORY_LABEL, Signal, SignalCategory } from "../signalTypes";
import SignalWorklist from "./SignalWorklist";

interface RoomSignalsTabProps {
    signals: Signal[];
    initialCategory?: SignalCategory | null;
    /** WP-4 (D10): activate the row's signal on the banner instead of opening
     *  a modal. Every row here already carries this room's cell relation
     *  (it came from `useSignals({ facility_location_id: cell })`), so it
     *  never needs the D4 no-room fallback — see `RipeningRoom/index.tsx`. */
    onActivate: (signal: Signal) => void;
    /** D13: the id of the signal currently on the banner, so the row it came
     *  from can be marked. Comes from `useActiveSignal`'s settled, room-matched
     *  `activeSignal` — see `RipeningRoom/index.tsx`. */
    activeSignalId?: string;
}

/** The room-scoped signals worklist (open signals for this cell). Rows
 *  activate the floating ActiveSignalBar — no modal (D10). */
export const RoomSignalsTab: React.FC<RoomSignalsTabProps> = ({ signals, initialCategory, onActivate, activeSignalId }) => {
    const [category, setCategory] = useState<SignalCategory | null>(initialCategory ?? null);
    useEffect(() => setCategory(initialCategory ?? null), [initialCategory]);

    const selection = useSignalSelection(); // required by the worklist; selectable=false here

    const visible = useMemo(
        () => filterAndSortSignals(signals, {
            ...DEFAULT_FILTER_STATE,
            view: "open",
            categories: category ? [category] : [],
        }),
        [signals, category],
    );

    return (
        <div className="d-flex flex-column gap-2">
            {category && (
                <div className="small d-flex align-items-center gap-2">
                    <Badge color={CATEGORY_COLOR[category]}>{CATEGORY_LABEL[category]}</Badge>
                    <span role="button" className="text-decoration-underline" onClick={() => setCategory(null)}>
                        clear filter
                    </span>
                </div>
            )}

            <SignalWorklist
                signals={visible}
                activeSignalId={activeSignalId}
                onRowClick={onActivate}
                selection={selection}
                selectable={false}
            />
        </div>
    );
};

export default RoomSignalsTab;
